import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBalanceScale, faTimes } from '@fortawesome/free-solid-svg-icons';

// Define the ComparePopup component
// It receives props needed to display and manage the popup
const ComparePopup = ({
  isOpen, // boolean to control visibility
  onClose, // function to close the popup
  quotations, // the quotations data (specifically quotations.standard)
  project, // the current project object
  productCategories, // list of product categories
  onViewDetailsClick, // function to handle clicking "View Details"
  selectedCategoryName
}) => {

  // If the popup is not open, return null (render nothing)
  if (!isOpen) {
    return null;
  }
//console.log(quotations)
  // Get all unique area names from all standard quotations
  const allAreaNames = quotations?.standard?.flatMap(standardPackage =>
    standardPackage.items?.map(item => {
      // Access area_name from application_areas (singular object)
      return item.application_areas?.area_name;
    })
  ).filter(Boolean) || [];

  // Remove duplicates
  const uniqueAreaNames = [...new Set(allAreaNames)];

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
        onClick={onClose} // Close when clicking outside
      ></div>

      {/* Popup Content - Coming from bottom, 80% height */}
      <div className="fixed bottom-0 left-0 right-0 w-full bg-white rounded-t-xl shadow-2xl z-50 overflow-hidden flex flex-col h-[80vh] animate-slide-up">
        {/* Header - Fixed */}
        <div className="border-b border-gray-300 text-white p-4 flex justify-between items-center">
          {/* Drag handle for mobile (optional, visual only) */}
          <div className="absolute top-1 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-white/30 rounded-full"></div>

          <h3 className="text-[#D64541] text-lg font-bold flex items-center">
            <FontAwesomeIcon icon={faBalanceScale} className="mr-2" />
            Compare Quotations
          </h3>
          <button
            onClick={onClose} // Close button
            className="text-[#D64541] text-md p-1 rounded-full hover:bg-white/20 transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Content with Scrollable Table */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Table Container with scrollable content */}
          <div className="relative flex-1 overflow-hidden">
            {/* Scrollable Table with Fixed First Column */}
            <div className="overflow-auto h-full">
              <table className="min-w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    {/* Sticky header for the first column */}
                    <th className="sticky left-0 top-0 bg-gray-50 p-3 border-b border-r border-black/50  text-left text-sm font-semibold text-gray-700 min-w-[120px] z-20">
                      {/* Empty header cell for the row labels */}
                    </th>
                    {/* Sticky headers for each standard package */}
                    {quotations.standard && quotations.standard.map((standardPackage, i) => (
                      <th key={i} className="sticky top-0 bg-gray-50 p-3 border-b border-r border-black/50 text-center min-w-[140px] z-10">
                        <div className="flex flex-col items-center">
                          <span className="text-lg font-semibold text-gray-800 line-clamp-1">
                            {standardPackage.package_name || `Standard ${i + 1}`}
                          </span>
                          <div className='text-sm text-[#14A311]'>
                            ₹{Math.round(standardPackage.subtotal).toLocaleString('en-IN')}/-
                          </div>
                          <button
                            className="text-blue-400 font-regular mt-1 text-xs bg-gray-50 hover:bg-gray-100 px-2 py-1 rounded relative"
                            // Call the passed-in handler
                            onClick={() => onViewDetailsClick(
                              project,
                              standardPackage._id,
                              productCategories.find(cat => cat._id === standardPackage.product_category_id), // Find category by ID from package
                              "Standard Quotation"
                            )}
                          >
                            <span className="relative inline-block">
                              View Details
                              <span className="absolute bottom-0 left-0 w-full border-b-2 border-dotted border-blue-400"></span>
                            </span>
                          </button>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Map each unique area name to a table row */}
                  {uniqueAreaNames.map((areaName, rowIndex) => (
                    <tr key={`area-${rowIndex}`} className="hover:bg-gray-50">
                      {/* Sticky cell for the area name */}
                      <td className="text-sm sticky left-0 bg-gray-50 hover:bg-gray-50 p-3 border-b border-r border-black/50 font-semibold text-gray-700 min-w-[80px] z-10">
                        {areaName}
                      </td>

                      {/* Map each standard package to a table cell */}
                      {quotations.standard && quotations.standard.map((standardPackage, colIndex) => {
                        // Find items in this package that match the current area name
                        const matchingItems = standardPackage.items?.filter(item =>
                          item.application_areas?.area_name === areaName
                        ) || [];

                        return (
                          <td key={`col-${colIndex}`} className="py-3 border-b border-r border-black/50 ">
                          {matchingItems.length > 0 ? (
  <div className="text-center">
    {matchingItems.map((item, itemIndex) => {
      // Find size and finish attributes
      const sizeAttr = item.attributes?.find(attr =>
        attr.attribute_name?.toLowerCase() === 'size'
      );
      const finishAttr = item.attributes?.find(attr =>
        attr.attribute_name?.toLowerCase() === 'finish'
      );

      return (
        <div key={`item-${itemIndex}`} className="mb-3 last:mb-0">
          <div className="px-2 flex flex-row items-center font-medium text-sm text-gray-800 mb-1 text-center justify-center">
            {/* Vendor image is common for all categories */}
            {item?.vendor?.image && (
              <img 
                src={process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES + item.vendor.image} 
                alt={item.vendor?.name || 'Vendor Image'} 
                className="border border-2 w-8 h-8 rounded-md mr-2" 
              />
            )}
            
            {/* Conditional rendering based on selectedCategoryName */}
            {selectedCategoryName?.toLowerCase() === 'tiles' ? (
              <p className='line-clamp-3'>{item.vendor?.name || 'Unnamed Vendor'}</p>
            ) : selectedCategoryName?.toLowerCase() === 'doors' ? (
              <p className='line-clamp-3'>{item.item_name || 'Unnamed Item'}</p>
            ) : selectedCategoryName?.toLowerCase() === 'windows' ? (
              <p className='line-clamp-3'>{item.item_name || 'Unnamed Item'}</p>
            ) : (
              <p className='line-clamp-3'>{item.vendor?.name || item.item_name || 'Unnamed Item'}</p>
            )}
          </div>
          
          {/* Only show separator for Tiles category */}
          {selectedCategoryName?.toLowerCase() === 'tiles' && (
            <div className='border-b border-gray-200 mb-3 mt-2'></div>
          )}
          
          {/* Only show size and finish attributes for Tiles */}
          {selectedCategoryName?.toLowerCase() === 'tiles' && (sizeAttr || finishAttr) && (
            <div className="flex flex-wrap gap-2 justify-center">
              {sizeAttr && (
                <span className="bg-gray-100 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-gray-800 border border-gray-200">
                  {sizeAttr.attribute_value}
                </span>
              )}
              {finishAttr && (
                <span className="bg-gray-100 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-gray-800 border border-gray-200">
                  {finishAttr.attribute_value}
                </span>
              )}
            </div>
          )}
        </div>
      );
    })}
  </div>
) : (
  <span className="text-xs text-gray-400 block text-center">Not applied</span>
)}


                          </td>
                        );
                      })}
                    </tr>
                  ))}

{/*                   <tr key="subtotal-row" className="bg-gray-50">
                    <td className="text-xs sticky left-0 bg-gray-50 p-3 border-b border-r border-gray-200 font-bold text-gray-700 min-w-[80px] z-10">
                    </td>

                  </tr> */}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/*
        Add this animation definition to your CSS file or tailwind.config.js.
        Including it here in a <style jsx> block for direct migration,
        but external CSS or Tailwind configuration is generally preferred.
      */}
      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }

        .animate-slide-up {
          animation: slide-up 0.3s ease-out forwards;
        }
      `}</style>
    </>
  );
};

export default ComparePopup;
